import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { getPenaltyMetricsByRegion, EventWithPenaltyMetrics } from '../../lib/api';

interface RegionEventStatusProps {
  // No props needed - self-contained
}

export const RegionEventStatus: React.FC<RegionEventStatusProps> = () => {
  const [selectedRegion, setSelectedRegion] = useState<'EU' | 'APAC' | 'AFRICA' | 'AMERICAS' | null>(null);
  const [eventsWithMetrics, setEventsWithMetrics] = useState<EventWithPenaltyMetrics[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [dateThreshold, setDateThreshold] = useState<string>('');

  // Format date to YYYY-MM-DD
  const formatDateForAPI = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date to DD-mmm-YYYY (e.g., "15-Jan-2024")
  const formatDateForDisplay = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Fetch events by region when region is selected or date threshold changes
  useEffect(() => {
    const fetchRegionEvents = async () => {
      if (!selectedRegion) {
        setEventsWithMetrics([]);
        return;
      }

      setIsLoadingEvents(true);
      try {
        // Only pass datethreshold if it's been set by the user
        const result = await getPenaltyMetricsByRegion(
          selectedRegion,
          dateThreshold ? formatDateForAPI(dateThreshold) : undefined
        );
        if (result.success) {
          setEventsWithMetrics(result.events);
        } else {
          console.error('Failed to fetch penalty metrics by region:', result.error);
          setEventsWithMetrics([]);
        }
      } catch (error) {
        console.error('Error fetching penalty metrics by region:', error);
        setEventsWithMetrics([]);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchRegionEvents();
  }, [selectedRegion, dateThreshold]);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Event Status by Region</h2>
        </div>

        {/* Region Filter Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Select Region :</h3>
          <div className="grid grid-cols-4 gap-4">
            <Card
              className={`cursor-pointer transition-all duration-200 ${
                selectedRegion === 'EU'
                  ? 'ring-2 ring-black shadow-lg bg-gray-100'
                  : 'hover:shadow-md bg-white'
              }`}
              onClick={() => setSelectedRegion('EU')}
            >
              <CardContent className="p-4">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900">EU</h4>
                </div>
              </CardContent>
            </Card>
            <Card
              className={`cursor-pointer transition-all duration-200 ${
                selectedRegion === 'APAC'
                  ? 'ring-2 ring-black shadow-lg bg-gray-100'
                  : 'hover:shadow-md bg-white'
              }`}
              onClick={() => setSelectedRegion('APAC')}
            >
              <CardContent className="p-4">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900">APAC</h4>
                </div>
              </CardContent>
            </Card>
            <Card
              className={`cursor-pointer transition-all duration-200 ${
                selectedRegion === 'AFRICA'
                  ? 'ring-2 ring-black shadow-lg bg-gray-100'
                  : 'hover:shadow-md bg-white'
              }`}
              onClick={() => setSelectedRegion('AFRICA')}
            >
              <CardContent className="p-4">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900">AFRICA</h4>
                </div>
              </CardContent>
            </Card>
            <Card
              className={`cursor-pointer transition-all duration-200 ${
                selectedRegion === 'AMERICAS'
                  ? 'ring-2 ring-black shadow-lg bg-gray-100'
                  : 'hover:shadow-md bg-white'
              }`}
              onClick={() => setSelectedRegion('AMERICAS')}
            >
              <CardContent className="p-4">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900">AMERICAS</h4>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Status of Events in the region</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 italic font-small">Previous Events by Date : </span>
              <Input
                type="date"
                value={dateThreshold}
                onChange={(e) => setDateThreshold(e.target.value)}
                className="w-40 h-9 text-sm"
                placeholder="Select date threshold"
              />
              {dateThreshold && (
                <button
                  onClick={() => setDateThreshold('')}
                  className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {!selectedRegion ? (
            <div className="text-center py-8 text-gray-500">
              Please select a region to view events
            </div>
          ) : isLoadingEvents ? (
            <div className="text-center py-8 text-gray-500">
              Loading events...
            </div>
          ) : eventsWithMetrics.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No events found for {selectedRegion} region
            </div>
          ) : (
            <div className="space-y-2">
              {/* Table Header */}
              <div className="grid grid-cols-5 gap-4 items-center p-3 bg-gray-100 rounded-lg font-semibold text-gray-700 text-sm">
                <div>Events</div>
                <div className="text-center">Total</div>
                <div className="text-center">Warnings</div>
                <div className="text-center">Penalties</div>
                <div>Active Judge List</div>
              </div>
              
              {/* Table Rows */}
              {eventsWithMetrics
                .slice()
                .sort((a, b) => {
                  const dateA = a.eventdate ? new Date(a.eventdate).getTime() : 0;
                  const dateB = b.eventdate ? new Date(b.eventdate).getTime() : 0;
                  
                  if (dateA !== dateB) {
                    return dateA - dateB;
                  }
                  
                  const nameA = a.eventname || '';
                  const nameB = b.eventname || '';
                  return nameA.localeCompare(nameB);
                })
                .map((event) => (
                  <div 
                    key={event.eventid} 
                    className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="grid grid-cols-5 gap-4 items-center">
                      <div>
                        <div className="font-medium text-gray-900">{event.eventname}</div>
                        {event.eventdate && (
                          <div className="text-sm text-gray-600 mt-1">
                            {formatDateForDisplay(event.eventdate)}
                          </div>
                        )}
                      </div>
                      <div className="text-center font-medium text-gray-900">
                        {event.totalpenalties}
                      </div>
                      <div className="text-center font-medium text-orange-600">
                        {event.totalwarnings}
                      </div>
                      <div className="text-center font-medium text-green-600">
                        {event.totalapproved}
                      </div>
                      <div>
                        {event.activejudgelist && event.activejudgelist.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {event.activejudgelist.map((judge, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                              >
                                {judge.replace('@hyrox.com', '')}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

